import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import makeStyles from '@mui/styles/makeStyles';
import { FormattedMessage } from 'react-intl';
import { useQuery } from '../../routes/useQuery';
import { ReactComponent as ForbiddenIcon } from './ForbiddenIcon.svg';

const PRICING_URL = 'https://katalon.com/pricing';
const BOOK_A_CALL_URL = 'https://katalon.com/view-a-demo';

const useStyles = makeStyles(theme => ({
  // --- Legacy "upgrade blocked" variant ---
  title: {
    textTransform: 'uppercase',
    fontWeight: 'bold',
  },
  center: {
    textAlign: 'center',
  },
  root: {
    marginTop: theme.spacing(10),
  },
  // --- No purchase permission design ---
  page: {
    flex: 1,
    minWidth: 0,
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    background: '#f6f6f5',
    color: '#16141d',
    overflowY: 'auto',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    height: 56,
    padding: '0 24px',
    background: '#fff',
    borderBottom: '1px solid #ececf4',
    flex: 'none',
  },
  brand: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 9,
  },
  brandName: {
    fontSize: 20,
    fontWeight: 700,
    letterSpacing: '-0.02em',
    color: '#191736',
  },
  main: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '56px 24px 72px',
  },
  content: {
    width: '100%',
    maxWidth: 960,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  lockBadge: {
    width: 96,
    height: 96,
    borderRadius: '50%',
    background: '#edf8f3',
    border: '1px solid #cfeadd',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 'none',
  },
  heading: {
    margin: '26px 0 0',
    textAlign: 'center',
    fontSize: 32,
    lineHeight: 1.15,
    fontWeight: 600,
    letterSpacing: '-0.02em',
    color: '#16141d',
  },
  subheading: {
    margin: '14px 0 0',
    maxWidth: 560,
    textAlign: 'center',
    fontSize: 15.5,
    lineHeight: 1.6,
    color: '#5f5d75',
  },
  cards: {
    display: 'flex',
    gap: 16,
    width: '100%',
    marginTop: 38,
    '@media (max-width:820px)': {
      flexDirection: 'column',
    },
  },
  card: {
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
    flex: '1 1 0',
    minWidth: 240,
    padding: 24,
    background: '#fff',
    border: '1px solid #e6e4ef',
    borderRadius: 16,
    transition: 'transform .15s ease, border-color .15s ease, background .15s ease',
    '&:hover': {
      transform: 'translateY(-2px)',
    },
  },
  cardAccent: {
    borderColor: '#0f8461',
    background: '#f4fbf8',
  },
  cardIcon: {
    width: 48,
    height: 48,
    borderRadius: 14,
    background: '#edf8f3',
    color: '#0f8461',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 'none',
  },
  cardTitle: {
    margin: 0,
    fontSize: 17,
    fontWeight: 700,
    letterSpacing: '-0.01em',
    color: '#16141d',
  },
  cardText: {
    margin: 0,
    fontSize: 13.5,
    lineHeight: 1.5,
    color: '#73718a',
  },
  cardCta: {
    marginTop: 'auto',
    paddingTop: 4,
  },
  btnBase: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 7,
    fontSize: 13.5,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
    transition: 'opacity .15s ease',
    '&:hover': {
      opacity: 0.85,
    },
  },
  btnFilled: {
    justifyContent: 'center',
    width: '100%',
    height: 42,
    padding: '0 18px',
    border: 'none',
    borderRadius: 10,
    color: '#fff',
    background: '#0f8461',
  },
  btnGhost: {
    width: 'auto',
    height: 'auto',
    padding: '2px 0',
    border: 'none',
    background: 'none',
    color: '#0f8461',
    fontWeight: 700,
  },
}));

const Forbidden = () => {
  const classes = useStyles();
  const { get } = useQuery();
  const isUpgradeBlocked = get('upgradeBlocked') === 'true';

  if (isUpgradeBlocked) {
    return (
      <Grid container justifyContent="center" className={classes.root}>
        <Grid item className={classes.center}>
          <ForbiddenIcon />
          <Typography variant="h2" className={classes.title}>
            <FormattedMessage id="forbidden.upgrade_blocked.title" />
          </Typography>
          <Typography variant="h3">
            <FormattedMessage id="forbidden.upgrade_blocked.explanation" />
          </Typography>
        </Grid>
      </Grid>
    );
  }

  const openExternal = (url: string) => () => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className={classes.page}>
      <header className={classes.header}>
        <span className={classes.brand}>
          <svg width="22" height="33" viewBox="0 0 72 108" fill="none" aria-hidden="true">
            <path d="M71.36 71.35H35.68v35.68h35.68z" fill="#19D89F" />
            <path d="M35.67 0 0 35.68v35.67h35.67L71.35 35.68V0z" fill="#191736" />
          </svg>
          <span className={classes.brandName}>Katalon</span>
        </span>
      </header>

      <main className={classes.main}>
        <div className={classes.content}>
          <span className={classes.lockBadge}>
            <svg
              width="42"
              height="42"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#0f8461"
              strokeWidth="1.55"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <rect x="5" y="10.5" width="14" height="10.5" rx="2.6" />
              <path d="M8.5 10.5V7a3.5 3.5 0 0 1 7 0v3.5" />
              <path d="M12 14.7v2.6" />
            </svg>
          </span>

          <Typography variant="h1" component="h1" className={classes.heading}>
            <FormattedMessage id="forbidden.no_purchase.title" />
          </Typography>
          <Typography className={classes.subheading}>
            <FormattedMessage id="forbidden.no_purchase.description" />
          </Typography>

          <div className={classes.cards}>
            <div className={`${classes.card} ${classes.cardAccent}`}>
              <span className={classes.cardIcon}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M20.6 13.4l-7.2 7.2a1.5 1.5 0 0 1-2.1 0l-6.4-6.4a1.5 1.5 0 0 1-.4-1V6.5A1.5 1.5 0 0 1 6 5h6.7a1.5 1.5 0 0 1 1 .4l6.9 6.9a1.5 1.5 0 0 1 0 2.1z" />
                  <circle cx="9" cy="9" r="1.25" fill="currentColor" stroke="none" />
                </svg>
              </span>
              <div>
                <h2 className={classes.cardTitle}>
                  <FormattedMessage id="forbidden.no_purchase.pricing.title" />
                </h2>
                <p className={classes.cardText}>
                  <FormattedMessage id="forbidden.no_purchase.pricing.description" />
                </p>
              </div>
              <div className={classes.cardCta}>
                <button
                  type="button"
                  className={`${classes.btnBase} ${classes.btnFilled}`}
                  onClick={openExternal(PRICING_URL)}
                >
                  <FormattedMessage id="forbidden.no_purchase.pricing.cta" />
                  {' '}
                  <span aria-hidden="true">&rarr;</span>
                </button>
              </div>
            </div>

            <div className={classes.card}>
              <span className={classes.cardIcon}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <rect x="4" y="6" width="16" height="14" rx="2.5" />
                  <path d="M4 10h16" />
                  <path d="M8 3.5v4" />
                  <path d="M16 3.5v4" />
                </svg>
              </span>
              <div>
                <h2 className={classes.cardTitle}>
                  <FormattedMessage id="forbidden.no_purchase.call.title" />
                </h2>
                <p className={classes.cardText}>
                  <FormattedMessage id="forbidden.no_purchase.call.description" />
                </p>
              </div>
              <div className={classes.cardCta}>
                <button
                  type="button"
                  className={`${classes.btnBase} ${classes.btnGhost}`}
                  onClick={openExternal(BOOK_A_CALL_URL)}
                >
                  <FormattedMessage id="forbidden.no_purchase.call.cta" />
                  {' '}
                  <span aria-hidden="true">&rarr;</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Forbidden;
